import React, { useEffect, useState } from 'react';
import { useWebHaptics } from "web-haptics/react";
import { useAuth } from '../contexts/AuthContext';
import Timer from './Timer';
import '../styles/GameOver.css';

function GameOver({ won, guessResults, correctAnswer, onShare, onClose, onOpenAuth }) {
  const { trigger } = useWebHaptics();
  const { isAuthenticated, stats, refreshStats } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Trigger haptic feedback when modal opens
    if (won) {
      // Win haptic: 5-burst celebratory pattern
      trigger([
        { duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
        { delay: 40, duration: 40 },
      ], { intensity: 1 });
    } else {
      // Lose haptic: long vibration
      trigger([
        { duration: 1000 },
      ], { intensity: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only trigger once when component mounts

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      refreshStats().finally(() => setLoading(false));
    }
  }, [isAuthenticated, refreshStats]); // refreshStats is now memoized in AuthContext

  const handleBackdropClick = (e) => {
    if (e.target.className === 'game-over-backdrop') {
      onClose();
    }
  };

  const currentGuessCount = guessResults.length;

  const renderDistributionBar = (count, maxCount, guessNum) => {
    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
    const isCurrentGuess = currentGuessCount === guessNum;

    return (
      <div className="distribution-row" key={guessNum}>
        <div className="distribution-label">{guessNum}</div>
        <div className="distribution-bar-container">
          <div 
            className={`distribution-bar ${isCurrentGuess ? 'current' : ''}`}
            style={{ width: `${Math.max(percentage, count > 0 ? 5 : 0)}%` }}
          >
            {count > 0 && <span className="distribution-count">{count}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    if (!isAuthenticated) {
      return null;
    }

    if (loading || !stats) {
      return (
        <div className="gameover-stats">
          <div className="stats-loading">Loading stats...</div>
        </div>
      );
    }

    const distribution = stats.guess_distribution || {};
    const guessData = [
      { num: 1, count: distribution.one || 0 },
      { num: 2, count: distribution.two || 0 },
      { num: 3, count: distribution.three || 0 },
      { num: 4, count: distribution.four || 0 },
      { num: 5, count: distribution.five || 0 },
      { num: 6, count: distribution.six || 0 },
    ];
    
    const maxCount = Math.max(...guessData.map(d => d.count), 1);

    return (
      <div className="gameover-stats">
        <div className="stats-divider">YOUR STATS</div>
        
        <div className="stats-summary-compact">
          <div className="stat-item-compact">
            <div className="stat-value-compact">{stats.total_games}</div>
            <div className="stat-label-compact">Played</div>
          </div>
          <div className="stat-item-compact">
            <div className="stat-value-compact">{stats.win_percentage}%</div>
            <div className="stat-label-compact">Win %</div>
          </div>
          <div className="stat-item-compact">
            <div className="stat-value-compact">{stats.current_streak}</div>
            <div className="stat-label-compact">Streak</div>
          </div>
          <div className="stat-item-compact">
            <div className="stat-value-compact">{stats.best_streak}</div>
            <div className="stat-label-compact">Max</div>
          </div>
        </div>

        <div className="stats-distribution-compact">
          <h4>Guess Distribution</h4>
          <div className="distribution-chart-compact">
            {guessData.map(data => renderDistributionBar(data.count, maxCount, data.num))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="game-over-backdrop" onClick={handleBackdropClick}>
      <div className="game-over">
        <button className="game-over-close" onClick={onClose}>×</button>
        
        <h2>{won ? '🏆 PORTAL MASTER' : 'TRY AGAIN 💫'}</h2>
        <p>{won ? "You found today's Skylander!" : 'Better luck tomorrow!'}</p>
        
        {!won && correctAnswer && (
          <div className="correct-answer-reveal">
            <p className="answer-label">Today's Skylander is:</p>
            <p className="answer-name">{correctAnswer}</p>
          </div>
        )}
        
        <button onClick={onShare} className="share-btn">
          Share Score
        </button>

        {renderStats()}

        {!isAuthenticated && (
          <div className="guest-auth-cta">
            <div className="cta-divider">WANT TO TRACK YOUR STATS?</div>
            <p className="cta-message">
              Sign in to save your progress and compete with friends!
            </p>
            <button onClick={onOpenAuth} className="cta-btn">
              Create Account / Log In
            </button>
          </div>
        )}
        
        <p className="timer-text">
          New Skylander in: <Timer />
        </p>
      </div>
    </div>
  );
}

export default GameOver;
